// v3/app/router/lazy-with-retry.ts — Chunk-load resilience for React.lazy
// A lazy chunk can fail to fetch when a deploy changed the hashes, the field
// network hiccups, or the dev server switched protocol under a stale tab.
// Strategy: retry the import once after a short pause; if it still fails,
// force ONE full reload (fresh HTML → fresh chunk manifest). A sessionStorage
// flag keyed by chunk prevents reload loops — after that, rethrow so the
// error boundary shows instead of a dead screen.
// Zero imports from v1/v2.

import { lazy, type ComponentType } from 'react';

const RETRY_DELAY_MS = 1200;
const RELOADED_KEY_PREFIX = 'v3-chunk-reloaded:';

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  chunkName: string,
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    const reloadedKey = `${RELOADED_KEY_PREFIX}${chunkName}`;
    try {
      const module = await factory();
      sessionStorage.removeItem(reloadedKey);
      return module;
    } catch (firstError) {
      // Transient network failure — one quiet retry.
      await sleep(RETRY_DELAY_MS);
      try {
        const module = await factory();
        sessionStorage.removeItem(reloadedKey);
        return module;
      } catch (secondError) {
        // Stale chunk manifest (new deploy / protocol change): a full reload
        // fetches fresh HTML with the current hashes. Only once per chunk.
        if (!sessionStorage.getItem(reloadedKey)) {
          sessionStorage.setItem(reloadedKey, '1');
          window.location.reload();
          // Keep Suspense pending while the reload happens.
          return new Promise<never>(() => {});
        }
        throw secondError;
      }
    }
  });
}
